<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  conflicts: {
    type: Array,
    default: () => [],
  },
  contactDecisions: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'apply-decisions',
  'cancel',
  'update:contact-decisions',
]);

const { t } = useI18n();
const dialogRef = ref(null);

const handleAccountDecision = (contactId, businessAccountId, decision) => {
  const updatedDecisions = { ...props.contactDecisions };
  if (!updatedDecisions[contactId]) {
    updatedDecisions[contactId] = {};
  }
  updatedDecisions[contactId][businessAccountId] = decision;
  emit('update:contact-decisions', updatedDecisions);
};

const getAccountDecision = (contactId, businessAccountId) => {
  return props.contactDecisions[contactId]?.[businessAccountId] || null;
};

const hasAllDecisions = computed(() => {
  if (props.conflicts.length === 0) return false;
  return props.conflicts.every(conflict => {
    const contactId = conflict.contact.id;
    const accountDecisions = props.contactDecisions[contactId] || {};

    return conflict.conflicting_business_accounts.every(account => {
      const decision = accountDecisions[account.id];
      return decision !== undefined && decision !== null;
    });
  });
});

const handleApply = () => {
  emit('apply-decisions');
  dialogRef.value?.close();
};

const handleCancel = () => {
  emit('cancel');
  dialogRef.value?.close();
};

const open = () => {
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog ref="dialogRef" width="2xl">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-full flex items-center justify-center">
          <span class="i-ph-warning text-amber-600 size-5" />
        </div>
        <span class="text-lg font-semibold text-n-slate-12">
          {{ t('BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.TITLE') }}
        </span>
      </div>
      <div class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{ t('BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.DESCRIPTION') }}
        </p>
        <div class="flex flex-col gap-3 max-h-96 overflow-y-auto">
          <div
            v-for="(conflict, conflictIndex) in conflicts"
            :key="conflictIndex"
            class="p-4 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1"
          >
            <div class="flex items-start gap-3 mb-3">
              <Avatar :name="conflict.contact.name" :size="32" />
              <div class="flex flex-col gap-1 flex-1">
                <h4 class="font-medium text-n-slate-12">
                  {{ conflict.contact.name }}
                </h4>
                <p class="text-sm text-n-slate-11">
                  {{
                    t(
                      'BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.ALREADY_LINKED_TO'
                    )
                  }}
                </p>
              </div>
            </div>
            <div class="flex flex-col gap-2 ml-11">
              <div
                v-for="(
                  account, accountIndex
                ) in conflict.conflicting_business_accounts"
                :key="accountIndex"
                class="flex items-center justify-between gap-3 p-2 border border-n-weak rounded bg-n-alpha-2 dark:bg-n-solid-2"
              >
                <div class="flex flex-col gap-0.5 flex-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ account.name }}
                  </span>
                  <span v-if="account.domain" class="text-xs text-n-slate-10">
                    {{ account.domain }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    :label="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'keep_both'
                        ? t(
                            'BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.KEEP_BOTH_SELECTED'
                          )
                        : t(
                            'BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.KEEP_BOTH'
                          )
                    "
                    :color="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'keep_both'
                        ? 'blue'
                        : 'slate'
                    "
                    :variant="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'keep_both'
                        ? 'solid'
                        : 'outline'
                    "
                    size="sm"
                    :class="
                      getAccountDecision(conflict.contact.id, account.id) !==
                      'keep_both'
                        ? 'hover:bg-n-alpha-4 dark:hover:bg-n-solid-4 bg-n-alpha-3 dark:bg-n-solid-3'
                        : ''
                    "
                    @click="
                      handleAccountDecision(
                        conflict.contact.id,
                        account.id,
                        'keep_both'
                      )
                    "
                  />
                  <Button
                    :label="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'force'
                        ? t(
                            'BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.MOVE_FROM_THIS_SELECTED'
                          )
                        : t(
                            'BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.MOVE_FROM_THIS'
                          )
                    "
                    :color="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'force'
                        ? 'amber'
                        : 'slate'
                    "
                    :variant="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'force'
                        ? 'solid'
                        : 'outline'
                    "
                    size="sm"
                    :class="
                      getAccountDecision(conflict.contact.id, account.id) !==
                      'force'
                        ? 'hover:bg-n-alpha-4 dark:hover:bg-n-solid-4 bg-n-alpha-3 dark:bg-n-solid-3'
                        : ''
                    "
                    @click="
                      handleAccountDecision(
                        conflict.contact.id,
                        account.id,
                        'force'
                      )
                    "
                  />
                  <Button
                    :label="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'skip'
                        ? t(
                            'BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.SKIP_SELECTED'
                          )
                        : t('BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.SKIP')
                    "
                    :color="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'skip'
                        ? 'slate'
                        : 'slate'
                    "
                    :variant="
                      getAccountDecision(conflict.contact.id, account.id) ===
                      'skip'
                        ? 'solid'
                        : 'outline'
                    "
                    size="sm"
                    :class="
                      getAccountDecision(conflict.contact.id, account.id) !==
                      'skip'
                        ? 'hover:bg-n-alpha-4 dark:hover:bg-n-solid-4 bg-n-alpha-3 dark:bg-n-solid-3'
                        : ''
                    "
                    @click="
                      handleAccountDecision(
                        conflict.contact.id,
                        account.id,
                        'skip'
                      )
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.CANCEL')"
          variant="link"
          type="button"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="handleCancel"
        />
        <Button
          :label="t('BUSINESS_ACCOUNT_DETAILS.CONFLICT_DIALOG.APPLY_DECISIONS')"
          color="blue"
          :disabled="!hasAllDecisions"
          @click="handleApply"
        />
      </div>
    </template>
  </Dialog>
</template>
